// src/pages/grow-business/rescheduled/[id].tsx
import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FaCheckCircle, FaCalendarAlt, FaClock } from 'react-icons/fa';
import { AppointmentDetails } from '@/components/scheduler/types';

const RescheduledPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [appointment, setAppointment] = useState<AppointmentDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';

    useEffect(() => {
        // If we don't have the state from navigation, fetch the appointment
        if (!location.state?.success && id) {
            fetchAppointment();
        } else {
            setLoading(false);
        }
    }, [id, location.state]);

    const fetchAppointment = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/api/booking/${id}`);
            if (response.ok) {
                const data: AppointmentDetails = await response.json();
                setAppointment(data);
            }
        } catch (err) {
            console.error('Error fetching appointment:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e2ba57]"></div>
            </div>
        );
    }

    const formatDateTime = (isoString: string) => {
        const date = new Date(isoString);
        return {
            date: date.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }),
            time: date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short'
            })
        };
    };

    const appointmentDateTime = appointment
        ? formatDateTime(appointment.appointment_time)
        : location.state
            ? {
                date: new Date(`${location.state.newDate}T12:00:00`).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }),
                time: location.state.newTime
            }
            : { date: '', time: '' };

    return (
        <>
            <Helmet>
                <title>Appointment Rescheduled | Swift Lead Digital</title>
            </Helmet>

            <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto">
                    <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                        <div className="mb-8">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                                <FaCheckCircle className="w-10 h-10 text-green-500" />
                            </div>

                            <h1 className="text-3xl font-bold text-[#212A37] mb-2">
                                Appointment Rescheduled!
                            </h1>
                            <p className="text-gray-600">
                                Your appointment has been successfully rescheduled.
                            </p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-6 mb-8">
                            <h2 className="text-lg font-semibold text-[#212A37] mb-4">
                                New Appointment Details
                            </h2>

                            <div className="space-y-3 text-left max-w-sm mx-auto">
                                <div className="flex items-start">
                                    <FaCalendarAlt className="text-[#e2ba57] mt-1 mr-3 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm text-gray-500">Date</p>
                                        <p className="font-medium text-[#212A37]">{appointmentDateTime.date}</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <FaClock className="text-[#e2ba57] mt-1 mr-3 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm text-gray-500">Time</p>
                                        <p className="font-medium text-[#212A37]">{appointmentDateTime.time}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                            <p className="text-sm text-blue-800">
                                <strong>What's Next?</strong> We've sent an updated confirmation email with your new appointment details and Zoom link.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <a
                                href="/"
                                className="inline-block bg-[#e2ba57] text-[#212A37] px-6 py-3 rounded-md font-semibold hover:bg-[#d1a43f] transition-colors"
                            >
                                Return to Home
                            </a>

                            <p className="text-sm text-gray-500">
                                Need to make more changes?{' '}
                                <a
                                    href={`/grow-business/reschedule/${id}`}
                                    className="text-[#e2ba57] hover:underline"
                                >
                                    Reschedule again
                                </a>
                                {' or '}
                                <a
                                    href={`/grow-business/cancel/${id}`}
                                    className="text-[#e2ba57] hover:underline"
                                >
                                    cancel appointment
                                </a>
                            </p>
                        </div>

                        <div className="mt-8 pt-8 border-t border-gray-200">
                            <p className="text-sm text-gray-500">
                                Questions? Email us at{' '}
                                <a href="mailto:ralph.brooks@swiftleaddigital.com" className="text-[#e2ba57] hover:underline">
                                    info@swiftleaddigital.com
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RescheduledPage;